import React, { useRef } from 'react'

import { useNavigate } from 'react-router-dom'
import { CheckIcon } from '@chakra-ui/icons'
import {
  Button,
  ButtonProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react'

import { GoogleIcon } from '@/assets/icons'
import { NOTETAKER_EMAIL } from '@/consts'
import { useUser } from '@/react-query/user'
import { ClockIcon } from '@/views/assets/ClockIcon'
import { CopyIconSVG } from '@/views/assets/CopyIconSVG'

export const NotetakerInfo: React.FC<ButtonProps> = (props) => {
  const user = useUser()

  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isGoogleCalendarInfoOpen,
    onOpen: onGoogleCalendarInfoOpen,
    onClose: onGoogleCalendarInfoClose,
  } = useDisclosure()

  const navigate = useNavigate()
  return (
    <>
      <Menu>
        <MenuButton
          variant={'brand-solid-white'}
          minWidth={'auto'}
          as={Button}
          {...props}
        >
          Add notetaker bot to your call
        </MenuButton>
        <MenuList>
          <MenuItem onClick={onOpen}>
            <ClockIcon boxSize={5} /> &nbsp;&nbsp;&nbsp;Add a bot to a future
            call
          </MenuItem>
          {user.data?.GoogleData ? (
            <MenuItem
              onClick={() => {
                navigate('/settings')
              }}
            >
              <GoogleIcon boxSize={5} /> &nbsp;&nbsp;&nbsp;
              {user.data.GoogleData.email} connected
            </MenuItem>
          ) : (
            <MenuItem
              w={'280px'}
              onClick={() => {
                onGoogleCalendarInfoOpen()
              }}
            >
              <GoogleIcon boxSize={5} /> &nbsp;&nbsp;&nbsp;Connect Google
              Calendar
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      <NotetakerInfoModal isOpen={isOpen} onClose={onClose} />
      <GoogleCalendarInfoModal
        isOpen={isGoogleCalendarInfoOpen}
        onClose={onGoogleCalendarInfoClose}
      />
    </>
  )
}

const GoogleCalendarInfoModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const navigate = useNavigate()

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={'xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Connect Google Calendar</ModalHeader>

        <ModalCloseButton />
        <ModalBody>
          <p>📅 Automatically transcribe meetings from your calendar</p>
          <br />
          <p>📹 Events should have a Zoom or Google Meet link</p>
          <br />
          <p>✅ Scheduled interview will appear here on your home screen</p>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={() => {
              navigate('/settings')
            }}
            rounded={'1.25rem'}
            variant={'brand-solid'}
          >
            Go to settings
          </Button>
          <Button
            onClick={onClose}
            rounded={'1.25rem'}
            variant={'brand-solid-white'}
          >
            Not now
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const NotetakerInfoModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const user = useUser()

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={'xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Invite bot to record your interview</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <p>
            🗓️ Please invite <CopyEmailComponent email={NOTETAKER_EMAIL} /> to
            the calendar event
          </p>
          <br />
          <p>
            📧 Make sure your email <b>{user.data?.email}</b> is also in this
            event
          </p>
          <br />
          <p>📹 The event should have a Zoom or Google Meet link</p>
          <br />
          <p>✅ Scheduled interview will appear on your home screen</p>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} rounded={'1.25rem'} variant={'brand-solid'}>
            Great!
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const CopyEmailComponent = ({ email }: { email: string }) => {
  const [isRecentlyCopied, setIsRecentlyCopied] = React.useState<boolean>(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null) // Store the timeout reference

  return (
    <Button
      onClick={() => {
        setIsRecentlyCopied(true)

        // Clear the previous timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        // Copy the email to clipboard
        navigator.clipboard.writeText(email)

        // Set a new timeout
        timeoutRef.current = setTimeout(() => {
          setIsRecentlyCopied(false)
        }, 1000)
      }}
      h={'2rem'}
      mx={1}
      variant={'brand-solid-white'}
      rounded={'1.25rem'}
      rightIcon={
        isRecentlyCopied ? (
          <CheckIcon w={'1rem'} />
        ) : (
          <CopyIconSVG color={'black'} width={'1rem'} />
        )
      }
    >
      {email}
    </Button>
  )
}
